import * as React from 'react';
import { flatMap, fromPairs, groupBy, isEqual, isNil, upperFirst } from 'lodash';
import Form from 'antd/lib/form';
import Tabs from 'antd/lib/tabs';
import ConfigProvider from 'antd/lib/config-provider';
import { NamePath } from 'antd/lib/form/interface';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Cascader from 'antd/lib/cascader';
import Select from 'antd/lib/select';
import Radio from 'antd/lib/radio';
import TimePicker from 'antd/lib/time-picker';
import { dayjs } from '@/extendedDayjs';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Switch from 'antd/lib/switch';
import Popover from 'antd/lib/popover';
import Typography from 'antd/lib/typography';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import {
    AnalyticsProperty,
    BundleSizeConditionType,
    BundleSizeConditionUnit,
    BundleSizeIncreaseCondition,
    RelativeTiming,
    ReminderOffset,
    DependentOptions,
    IntegrationType,
    MicrosoftTeamsChannel,
    PatternToken,
    SettingField,
    SettingOption,
    SettingFieldType,
    SettingHelpTextBehavior,
    SettingRadioDirection,
    WorkflowType,
} from '../types';
import TooltipInfoIcon from '../components/helperComponents/TooltipInfoIcon';
import { PatternTokenHelperTextComponent } from '../components/appSettings/PatternTokenHelperTextComponent';
import SupportedTokensComponent from '../components/common/SupportedTokensComponent';
import { TeamsChannelOverrideSelect } from '../components/sharedComponents/TeamsChannelOverrideSelect';
import { antGrey, runwayColors } from '../components/styles/globalStyles';
import { EventsAndParamsInput } from '../components/integrations/EventsAndParamsInput';
import { ReminderOffsetsSettings } from '../components/sharedComponents/ReminderOffsetsSettings';
import { FieldType, filterOptionLabel, FormFieldData, renderFormField } from './formHelpers';

const INFO_ICON_GAP = 8;
const MAX_BUNDLE_SIZE_INCREASE_VALUE = 10000;
const SETTING_FIELD_MIN_ROW_WIDTH = 120;
const SETTING_FIELD_NUMBER_WIDTH = 200;
const SETTING_FIELD_TAB_CONTENT_STYLE: React.CSSProperties = {
    background: runwayColors.white,
    borderRadius: '0 0 8px 8px',
    padding: 16,
    border: `1px solid ${antGrey.grey4}`,
    borderTop: 'none',
};

// Keys are dot-separated payload paths, which antd writes back as nested objects
const getSettingFieldNamePath = (key: string): NamePath => (key.includes('.') ? key.split('.') : key);

// Returns a replacement for fields the shared inputs can't edit, like notification condition groups; undefined keeps the built field
export type SettingFormFieldResolver = (
    settingField: SettingField,
    formField: FormFieldData,
) => FormFieldData | undefined;

// The integration type only picks the events widget's wording
export const formFieldsFromSettingFields = (
    settingFields: SettingField[],
    integrationType?: IntegrationType,
    resolveFormField?: SettingFormFieldResolver,
): FormFieldData[] => {
    const variantLines: SettingField[][][] = [];
    for (const variants of Object.values(groupBy(settingFields, 'key'))) {
        const previousLine = variantLines[variantLines.length - 1];
        if (variants[0].isInlineWithPrevious && previousLine) {
            previousLine.push(variants);
        } else {
            variantLines.push([variants]);
        }
    }

    const lines = variantLines.map((lineVariants): SettingFieldLine => {
        const [firstVariants, ...restVariants] = lineVariants;
        const tab = firstVariants[0].tab;
        if (tab && restVariants.length === 0 && firstVariants[0].isLabelAboveTabs) {
            return {
                tab,
                tabLabelField: firstVariants[0],
                formFields: [
                    formFieldFromSettingFieldVariants(
                        firstVariants.map((field) => ({ ...field, helpText: undefined, isLabelHidden: true })),
                        integrationType,
                        resolveFormField,
                    ),
                ],
            };
        }
        const labelField = getSharedLabelField(lineVariants);
        const lineFields =
            labelField ?
                [
                    firstVariants.map((field) => ({ ...field, helpText: undefined, isLabelHidden: true })),
                    ...restVariants,
                ]
            :   lineVariants;
        return {
            tab,
            labelField,
            formFields: lineFields.map((variants) =>
                formFieldFromSettingFieldVariants(variants, integrationType, resolveFormField),
            ),
        };
    });
    return groupSettingFieldTabs(lines);
};

// When only the first field of a line has a label, it names the whole line, like a duration and its unit
const getSharedLabelField = (lineVariants: SettingField[][]): SettingField | undefined => {
    const [firstVariants, ...restVariants] = lineVariants;
    const firstField = firstVariants[0];
    const isOnlyFirstLabeled =
        restVariants.length > 0 &&
        !firstField.isLabelHidden &&
        !hasInlineLabel(firstField) &&
        restVariants.every((variants) => variants[0].isLabelHidden);
    return isOnlyFirstLabeled ? firstField : undefined;
};

const formFieldFromSettingFieldVariants = (
    variants: SettingField[],
    integrationType: IntegrationType | undefined,
    resolveFormField: SettingFormFieldResolver | undefined,
): FormFieldData => {
    const formField = formFieldFromSettingField(variants[0], integrationType, resolveFormField);
    const isConditional = variants.some(
        (field) =>
            field.visibleWhenEquals ||
            field.visibleWhenAnySet ||
            field.disabledWhenEquals ||
            field.disabledUnlessAnySet ||
            field.disabledReason,
    );
    return isConditional ? conditionalSettingField(formField, variants, integrationType, resolveFormField) : formField;
};

interface SettingFieldLine {
    tab?: string;
    tabLabelField?: SettingField;
    labelField?: SettingField;
    formFields: FormFieldData[];
}

interface SettingFieldTabPane {
    tab: string;
    formFields: FormFieldData[];
}

// Consecutive lines with tabs share one set of tabs, ordered by where each tab first appears; a field labelled above its tabs starts a new set when that label changes
const groupSettingFieldTabs = (lines: SettingFieldLine[]): FormFieldData[] => {
    const formFields: FormFieldData[] = [];
    let panes: SettingFieldTabPane[] = [];
    for (const [index, line] of lines.entries()) {
        const lineFormField = buildSettingFieldLine(line);
        if (!line.tab) {
            formFields.push(lineFormField);
            continue;
        }

        const pane = panes.find((existingPane) => existingPane.tab === line.tab);
        if (pane) {
            pane.formFields.push(lineFormField);
        } else {
            panes.push({ tab: line.tab, formFields: [lineFormField] });
        }

        const nextLine = lines[index + 1];
        const isLastTabbedLine = !nextLine?.tab || nextLine.tabLabelField?.label !== line.tabLabelField?.label;
        if (isLastTabbedLine) {
            formFields.push(buildSettingFieldTabs(panes, line.tabLabelField));
            panes = [];
        }
    }
    return formFields;
};

// Every tab stays mounted, so fields on other tabs still submit and validate
const buildSettingFieldTabs = (panes: SettingFieldTabPane[], labelField: SettingField | undefined): FormFieldData => ({
    fieldType: FieldType.WrappedGroup,
    // Listed flat so the form still seeds each tab's initial values
    groupedFields: flatMap(panes, (pane) => pane.formFields),
    groupChildrenWrapper: (_children, _fields, renderFields) =>
        labelField ?
            withSettingFieldLabel(
                labelField,
                <Tabs
                    items={panes.map((pane) => ({
                        key: pane.tab,
                        label: pane.tab,
                        forceRender: true,
                        children: renderFields(pane.formFields),
                    }))}
                />,
            )
        :   <Tabs
                type="card"
                tabBarStyle={{ marginBottom: 0 }}
                items={panes.map((pane) => ({
                    key: pane.tab,
                    label: pane.tab,
                    forceRender: true,
                    children: <div style={SETTING_FIELD_TAB_CONTENT_STYLE}>{renderFields(pane.formFields)}</div>,
                }))}
            />,
});

const withSettingFieldLabel = (
    labelField: SettingField | undefined,
    children: React.ReactNode,
    style?: React.CSSProperties,
): React.ReactNode => {
    if (!labelField) {
        return children;
    }
    const help = getSettingFieldHelp(labelField);
    return (
        <Form.Item
            label={labelField.label}
            tooltip={
                help.tooltip && {
                    title: help.tooltip,
                    icon: <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />,
                }
            }
            style={style}
        >
            {children}
        </Form.Item>
    );
};

// Fields of a row share it by their widths and wrap when it's too narrow; a hidden field leaves its space to the rest
const buildSettingFieldLine = ({ formFields, labelField }: SettingFieldLine): FormFieldData => {
    if (formFields.length === 1) {
        return formFields[0];
    }

    const labelHelp = labelField && getSettingFieldHelp(labelField);
    return {
        fieldType: FieldType.WrappedGroup,
        // A field without a width share stays compact, like a duration's number and unit
        groupedFields: formFields.map((formField) => ({
            ...formField,
            style: {
                // The shared label already sits in the row's top margin
                ...(labelField && { marginTop: 0 }),
                ...(formField.style ?? { width: SETTING_FIELD_MIN_ROW_WIDTH }),
            },
        })),
        helpMessage: labelHelp?.message,
        groupChildrenWrapper: (children) =>
            withSettingFieldLabel(
                labelField,
                <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 8, width: '100%' }}>{children}</div>,
                { marginTop: 12, marginBottom: 0 },
            ),
    };
};

const formFieldFromSettingField = (
    settingField: SettingField,
    integrationType: IntegrationType | undefined,
    resolveFormField: SettingFormFieldResolver | undefined,
): FormFieldData => {
    const formField = buildSettingFormField(settingField, integrationType);
    return resolveFormField?.(settingField, formField) ?? formField;
};

const buildSettingFormField = (
    settingField: SettingField,
    integrationType: IntegrationType | undefined,
): FormFieldData => {
    const help = getSettingFieldHelp(settingField);
    const isListEditor =
        settingField.type === SettingFieldType.KeyValue ||
        settingField.type === SettingFieldType.SecretKeyValue ||
        settingField.type === SettingFieldType.Rows;

    return {
        name: getSettingFieldNamePath(settingField.key),
        label: getSettingFieldLabel(settingField),
        tooltip: help.tooltip,
        initialValue: getSettingFieldInitialValue(settingField),
        fieldType: FieldType.Custom,
        rules: getSettingFieldRules(settingField),
        helpMessage: withSettingFieldResetButton(
            settingField,
            withSettingFieldWarning(settingField.warningText, help.message),
        ),
        extraFormItemProps: {
            ...(isBooleanSettingField(settingField) && { valuePropName: 'checked' }),
            // Validation messages name the field even when its label is hidden
            messageVariables: { label: settingField.label },
            // List editors hold several inputs, which shouldn't each show the field's feedback icon
            ...(isListEditor && { hasFeedback: false }),
            ...((settingField.requiredWhenAnySet || settingField.requiredUnlessAnySet) && {
                dependencies: [
                    ...(settingField.requiredWhenAnySet ?? []),
                    ...(settingField.requiredUnlessAnySet ?? []),
                ].map(getSettingFieldNamePath),
            }),
        },
        style:
            settingField.width !== undefined ?
                { flex: `${settingField.width} 1 0`, minWidth: SETTING_FIELD_MIN_ROW_WIDTH }
            :   undefined,
        renderField: () => renderSettingFieldInput(settingField, integrationType),
    };
};

const isBooleanSettingField = (settingField: SettingField): boolean =>
    settingField.type === SettingFieldType.Checkbox || settingField.type === SettingFieldType.Switch;

// Checkboxes and key/value lists show their own label and help text
const hasInlineLabel = (settingField: SettingField): boolean =>
    settingField.type === SettingFieldType.Checkbox || settingField.type === SettingFieldType.KeyValue;

const getSettingFieldLabel = (settingField: SettingField): string => {
    if (hasInlineLabel(settingField)) {
        return '';
    }
    if (settingField.isLabelHidden) {
        return '';
    }
    return settingField.label;
};

const getSettingFieldHelp = (settingField: SettingField): { tooltip?: string; message?: React.ReactNode } => {
    const { helpText, patternTokens, helpTextBehavior } = settingField;
    if (settingField.options?.some((option) => option.helpText)) {
        return { message: <SelectedOptionHelpText settingField={settingField} /> };
    }
    if (hasInlineLabel(settingField)) {
        return helpTextBehavior === SettingHelpTextBehavior.Below ? { message: helpText } : {};
    }
    // Key/value lists list their tokens themselves; other fields list them in place of the help text
    if (patternTokens) {
        // A text area's help reads before its tokens, while a one-line input keeps it in a tooltip
        const isHelpBeforeTokens = settingField.type === SettingFieldType.TextArea;
        return {
            tooltip: isHelpBeforeTokens ? undefined : helpText,
            message: (
                <SupportedTokensComponent
                    availableTokens={patternTokens}
                    prefixText={isHelpBeforeTokens ? helpText : undefined}
                    secondary
                />
            ),
        };
    }
    if (helpTextBehavior) {
        return helpTextBehavior === SettingHelpTextBehavior.Tooltip ? { tooltip: helpText } : { message: helpText };
    }
    // Fields sharing a row keep their help in a tooltip, so their inputs line up
    if (settingField.width !== undefined) {
        return { tooltip: helpText };
    }
    return { message: helpText };
};

const SelectedOptionHelpText: React.FC<{ settingField: SettingField }> = ({ settingField }) => {
    const value = Form.useWatch(getSettingFieldNamePath(settingField.key));
    const selectedOption = settingField.options?.find((option) => isEqual(option.value, value));
    return <>{selectedOption?.helpText ?? settingField.helpText}</>;
};

// Mirrors the backend's rows check, so a missing cell or a repeated row shows next to the field
const validateSettingRows = (settingField: SettingField, rows: Row[]): Promise<void> => {
    for (const [index, row] of rows.entries()) {
        const missingColumn = settingField.columns?.find(
            (column) => column.required && isEmptySettingValue(row[column.key]),
        );
        if (missingColumn) {
            return Promise.reject(new Error(`${missingColumn.label} of row ${index + 1} is required`));
        }
        if (rows.slice(0, index).some((previousRow) => isEqual(previousRow, row))) {
            return Promise.reject(new Error(`Row ${index + 1} is listed twice`));
        }
    }
    return Promise.resolve();
};

// antd only counts an empty list as missing for array rules
const getSettingFieldRules = (settingField: SettingField): FormFieldData['rules'] => {
    const requiredRule =
        isListSettingField(settingField) ? { required: true, type: 'array' as const } : { required: true };
    if (settingField.type === SettingFieldType.Rows) {
        return [{ validator: (_, rows: Row[] | undefined) => validateSettingRows(settingField, rows ?? []) }];
    }
    const valueRules = getSettingFieldValueRules(settingField);
    if (settingField.required) {
        return [requiredRule, ...valueRules];
    }
    const requiredWhenAnySet = settingField.requiredWhenAnySet ?? [];
    const requiredUnlessAnySet = settingField.requiredUnlessAnySet ?? [];
    if (requiredWhenAnySet.length === 0 && requiredUnlessAnySet.length === 0) {
        return valueRules;
    }
    return [
        ({ getFieldValue }) => ({
            ...requiredRule,
            required:
                isAnySettingSet(requiredWhenAnySet, getFieldValue) ||
                (requiredUnlessAnySet.length > 0 && !isAnySettingSet(requiredUnlessAnySet, getFieldValue)),
        }),
        ...valueRules,
    ];
};

// Mirror the backend's checks, so a broken reminder or a repeated condition type shows next to the field
const getSettingFieldValueRules = (settingField: SettingField): FormFieldData['rules'] => {
    switch (settingField.type) {
        case SettingFieldType.ReminderOffsets:
            return [
                { validator: (_, offsets: ReminderOffset[] | undefined) => validateReminderOffsets(offsets ?? []) },
            ];
        case SettingFieldType.BundleSizeConditions:
            return [
                {
                    validator: (_, conditions: BundleSizeIncreaseCondition[] | undefined) =>
                        validateBundleSizeConditionTypes(conditions ?? []),
                },
            ];
        case SettingFieldType.Input:
        case SettingFieldType.TextArea:
            return [
                ...(settingField.pattern ?
                    [{ pattern: new RegExp(settingField.pattern), message: upperFirst(settingField.patternMessage) }]
                :   []),
                ...(settingField.maxLength ?
                    [
                        {
                            max: settingField.maxLength,
                            message: `${settingField.label} must be ${settingField.maxLength} characters or less`,
                        },
                    ]
                :   []),
            ];
        default:
            return [];
    }
};

const validateReminderOffsets = (offsets: ReminderOffset[]): Promise<void> => {
    const hasEmptyBeforeRow = offsets.some(
        (offset) =>
            offset?.timing === RelativeTiming.before &&
            !(offset.days || 0) &&
            !(offset.hours || 0) &&
            !(offset.minutes || 0),
    );
    return hasEmptyBeforeRow ?
            Promise.reject(new Error('Set a time for each "before" reminder, or choose "at the time of"'))
        :   Promise.resolve();
};

const validateBundleSizeConditionTypes = (conditions: BundleSizeIncreaseCondition[]): Promise<void> => {
    const types = conditions.map((condition) => condition?.type).filter(Boolean);
    return new Set(types).size < types.length ?
            Promise.reject(new Error('You may only have one condition per type'))
        :   Promise.resolve();
};

const isAnySettingSet = (keys: string[], getFieldValue: (name: NamePath) => unknown): boolean =>
    keys.some((key) => !isEmptySettingValue(getFieldValue(getSettingFieldNamePath(key))));

const isListSettingField = (settingField: SettingField): boolean =>
    settingField.type === SettingFieldType.MultiSelect ||
    settingField.type === SettingFieldType.EventsAndParams ||
    settingField.type === SettingFieldType.Tags ||
    settingField.type === SettingFieldType.MultiCascader ||
    settingField.type === SettingFieldType.TeamsChannels ||
    settingField.type === SettingFieldType.ReminderOffsets ||
    settingField.type === SettingFieldType.BundleSizeConditions;

// A cascader's value is [parent, child]; its form item holds the child
const getSettingFieldInitialValue = (settingField: SettingField): unknown => {
    if (settingField.type === SettingFieldType.Cascader) {
        return settingField.currentValue?.[1];
    }
    return settingField.currentValue ?? settingField.defaultValue;
};

const renderSettingFieldInput = (
    settingField: SettingField,
    integrationType: IntegrationType | undefined,
): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Checkbox:
            return (
                <Checkbox>
                    {settingField.label}
                    {settingField.helpText && settingField.helpTextBehavior !== SettingHelpTextBehavior.Below && (
                        <span style={{ marginInlineStart: INFO_ICON_GAP }}>
                            <TooltipInfoIcon tooltipContent={settingField.helpText} />
                        </span>
                    )}
                </Checkbox>
            );
        case SettingFieldType.Switch:
            return <Switch checkedChildren="On" unCheckedChildren="Off" />;
        case SettingFieldType.Cascader:
            return <SettingFieldCascader settingField={settingField} />;
        case SettingFieldType.Select:
        case SettingFieldType.MultiSelect: {
            const mode = settingField.type === SettingFieldType.MultiSelect ? 'multiple' : undefined;
            if (settingField.optionsBy) {
                return <DependentOptionsSelect optionsBy={settingField.optionsBy} mode={mode} />;
            }
            return (
                <Select
                    options={getSelectOptions(settingField.options)}
                    mode={mode}
                    placeholder={settingField.placeholder ?? 'Select'}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    style={{ width: '100%' }}
                />
            );
        }
        case SettingFieldType.Tags:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    mode="tags"
                    tokenSeparators={['\n']}
                    // Without suggestions there's nothing to list, so only typed values show
                    open={settingField.options ? undefined : false}
                    notFoundContent={null}
                    placeholder={settingField.placeholder ?? (settingField.options ? 'Select or enter' : 'Enter')}
                    allowClear
                    filterOption={filterOptionLabel}
                    style={{ width: '100%' }}
                />
            );
        case SettingFieldType.Input:
            return <Input autoComplete="off" placeholder={settingField.placeholder} />;
        case SettingFieldType.TextArea:
            return <Input.TextArea rows={settingField.rows ?? 4} placeholder={settingField.placeholder} />;
        case SettingFieldType.MultiCascader:
            return (
                <Cascader
                    multiple
                    showSearch
                    allowClear
                    expandTrigger="hover"
                    placeholder="Select"
                    options={getCascaderOptions(settingField)}
                    displayRender={(labels) => `${labels[1]} (${labels[0]})`}
                    style={{ width: '100%' }}
                />
            );
        case SettingFieldType.TeamsChannels:
            return (
                <TeamsChannelOverrideSelect
                    allChannels={getTeamsChannelsFromOptions(settingField)}
                    selectedChannels={[]}
                    teams={(settingField.options ?? []).map((team) => ({
                        id: team.value,
                        name: team.label,
                        description: '',
                    }))}
                    disabled={false}
                />
            );
        case SettingFieldType.Rows:
            return <SettingRowsInput settingField={settingField} />;
        case SettingFieldType.SecretKeyValue:
            return (
                <KeyValueInput
                    itemLabel={settingField.itemLabel ?? settingField.label}
                    redactedKeys={Object.keys(settingField.currentValue ?? {})}
                />
            );
        case SettingFieldType.Radio:
            return (
                <Radio.Group>
                    {/* Side by side, a radio's own end margin already spaces it */}
                    <Space direction={settingField.radioDirection ?? SettingRadioDirection.Vertical} size={[0, 4]}>
                        {getSelectOptions(settingField.options)?.map((option) => (
                            <Radio
                                key={String(option.value)}
                                value={option.value}
                                disabled={option.disabled}
                                title={option.title}
                            >
                                {option.label}
                            </Radio>
                        ))}
                    </Space>
                </Radio.Group>
            );
        case SettingFieldType.Number:
        case SettingFieldType.Integer:
            return (
                <InputNumber
                    precision={settingField.type === SettingFieldType.Integer ? 0 : undefined}
                    min={settingField.min}
                    max={settingField.max}
                    placeholder={settingField.placeholder}
                    addonAfter={settingField.unitAddon}
                    // A number never needs the full width of the form, unless it has a share of its row.
                    // With a unit, the style would size the unit too, so antd's own input width keeps it compact.
                    style={
                        settingField.unitAddon && settingField.width === undefined ?
                            undefined
                        :   {
                                width: '100%',
                                maxWidth: settingField.width === undefined ? SETTING_FIELD_NUMBER_WIDTH : undefined,
                            }
                    }
                />
            );
        case SettingFieldType.EventsAndParams:
            return (
                integrationType && (
                    <EventsAndParamsInput
                        integrationId={integrationType}
                        eventsAndParams={getEventsAndParamsSuggestions(settingField)}
                    />
                )
            );
        case SettingFieldType.KeyValue:
            return (
                <KeyValueInput
                    itemLabel={settingField.label}
                    helpText={settingField.helpText}
                    patternTokens={settingField.patternTokens}
                />
            );
        case SettingFieldType.HourOfDay:
            return <HourOfDayInput />;
        case SettingFieldType.ReminderOffsets:
            return <ReminderOffsetsSettings eventLabel={settingField.itemLabel ?? ''} />;
        case SettingFieldType.BundleSizeConditions:
            return <BundleSizeConditionsInput typeOptions={settingField.options ?? []} />;
        // Its choices come from the app's monitoring settings and integrations, so its form supplies it through a resolver
        case SettingFieldType.ConditionGroups:
            return null;
        // Its destinations' icons come from the app's state, so its form supplies it through a resolver
        case SettingFieldType.DestinationOptions:
        case SettingFieldType.RetainedBuilds:
        case SettingFieldType.AgeRating:
            return null;
        // Its steps come from the app's step graph, so its form supplies it through a resolver
        case SettingFieldType.RequiredSteps:
            return null;
        // Its rows pair a milestone with statuses and hold the kickoff switch, so its form supplies it through a resolver
        case SettingFieldType.MilestoneStatuses:
            return null;
        case SettingFieldType.NumberMap:
            return <NumberMapInput settingField={settingField} />;
    }
};

// Edits a map of numbers as rows, each key picked from the options; rows without a key or value are left out of the value
const NumberMapInput: React.FC<{
    settingField: SettingField;
    value?: Record<string, number>;
    onChange?: (value: Record<string, number>) => void;
}> = ({ settingField, value, onChange }) => {
    const [rows, setRows] = React.useState<Row[]>(() => getKeyValueRows(value));
    // Resyncs when the form sets the value, but keeps rows the user is still filling in
    React.useEffect(() => {
        setRows((currentRows) =>
            isEqual(getNumberMapRowsValue(currentRows), value ?? {}) ? currentRows : getKeyValueRows(value),
        );
    }, [value]);

    return (
        <RowsEditor
            itemLabel={settingField.itemLabel ?? settingField.label}
            columns={[
                { key: 'key', getPlaceholder: () => 'Select', options: getSelectOptions(settingField.options) ?? [] },
                {
                    key: 'value',
                    getPlaceholder: () => 'Value',
                    numberBounds: { min: settingField.min, max: settingField.max },
                },
            ]}
            rows={rows}
            newRow={{}}
            onRowsChange={(updatedRows) => {
                setRows(updatedRows);
                onChange?.(getNumberMapRowsValue(updatedRows));
            }}
        />
    );
};

const getNumberMapRowsValue = (rows: Row[]): Record<string, number> =>
    fromPairs(
        rows
            .filter((row) => row.key != null && row.value != null)
            .map((row) => [row.key as string, row.value as number]),
    );

// Saved as a UTC hour and picked in local time; hour zero is a real value
const HourOfDayInput: React.FC<{ value?: number | null; onChange?: (value: number | null) => void }> = ({
    value,
    onChange,
}) => (
    <TimePicker
        format="h:00 A"
        showNow={false}
        style={{ width: '100%' }}
        value={value != null ? dayjs.utc().hour(value).minute(0).local() : null}
        onChange={(time) => onChange?.(time ? time.utc().hour() : null)}
    />
);

// Options are the condition types, one condition each
const BundleSizeConditionsInput: React.FC<{ typeOptions: SettingOption[] }> = ({ typeOptions }) => {
    const form = Form.useFormInstance();
    const enabledTypes = typeOptions.filter((option) => !option.disabledReason).map((option) => option.value);

    return (
        <Form.List name="bundleSizeIncreaseConditions">
            {(listFields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {listFields.map(({ key, name, ...rest }) => (
                        <div
                            key={key}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                                gap: 8,
                                width: '100%',
                            }}
                        >
                            <Form.Item
                                {...rest}
                                name={[name, 'type']}
                                rules={[{ required: true }]}
                                style={{ marginBottom: 0, minWidth: 140 }}
                            >
                                <Select placeholder="Type" options={getSelectOptions(typeOptions)} />
                            </Form.Item>
                            <Form.Item
                                name={[name, 'value']}
                                rules={[{ required: true, message: 'Value is required' }]}
                                style={{ marginBottom: 0, width: 100 }}
                            >
                                <InputNumber
                                    min={1}
                                    max={MAX_BUNDLE_SIZE_INCREASE_VALUE}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Form.Item
                                name={[name, 'unit']}
                                rules={[{ required: true }]}
                                style={{ marginBottom: 0, minWidth: 100 }}
                            >
                                <Select
                                    placeholder="Unit"
                                    options={[
                                        { value: BundleSizeConditionUnit.MB, label: 'MB' },
                                        { value: BundleSizeConditionUnit.Percent, label: '% change' },
                                    ]}
                                />
                            </Form.Item>
                            <Button
                                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                danger
                                type="link"
                                onClick={() => remove(name)}
                            />
                        </div>
                    ))}
                    <Button
                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                        disabled={listFields.length >= enabledTypes.length}
                        onClick={() => {
                            const conditions: BundleSizeIncreaseCondition[] =
                                form.getFieldValue('bundleSizeIncreaseConditions') ?? [];
                            const usedTypes = new Set(conditions.map((condition) => condition?.type));
                            add({
                                type:
                                    enabledTypes.find((type) => !usedTypes.has(type)) ??
                                    BundleSizeConditionType.Download,
                                unit: BundleSizeConditionUnit.MB,
                                value: 10,
                            });
                        }}
                    >
                        {listFields.length === 0 ? 'Add condition' : 'Add another condition'}
                    </Button>
                </Space>
            )}
        </Form.List>
    );
};

// The backend rejects a disabled option, so it can't be picked, and its reason shows on hover
const getSelectOptions = (options: SettingOption[] | null | undefined) =>
    options?.map((option) => ({ ...option, disabled: !!option.disabledReason, title: option.disabledReason }));

// Teams channels are offered per team, and the select lists them all
const getTeamsChannelsFromOptions = (settingField: SettingField): MicrosoftTeamsChannel[] =>
    flatMap(Object.values(settingField.optionsBy?.options ?? {}), (channels) =>
        channels.map((channel) => ({
            id: channel.value,
            name: channel.label,
            displayName: channel.label,
            isRedacted: false,
        })),
    );

// Every event suggests the same params; nil options mean no suggestions
const getEventsAndParamsSuggestions = (settingField: SettingField): Map<string, AnalyticsProperty[]> | undefined => {
    if (!settingField.options) {
        return undefined;
    }
    const params = (settingField.paramOptions ?? []).map((option) => ({
        id: String(option.value),
        name: option.label,
    }));
    return new Map(settingField.options.map((option) => [String(option.value), params]));
};

// Form.Item passes value and onChange through to the Select
const DependentOptionsSelect: React.FC<{
    optionsBy: DependentOptions;
    mode?: 'multiple';
    value?: unknown;
    onChange?: (value: unknown) => void;
}> = ({ optionsBy, mode, value, onChange }) => {
    const parentValue = Form.useWatch(getSettingFieldNamePath(optionsBy.field));
    const options = optionsBy.options?.[String(parentValue)] ?? [];

    // Drops values the new parent doesn't offer; skips the first parent value, which is the saved one loading in
    const previousParentValue = React.useRef(parentValue);
    React.useEffect(() => {
        if (previousParentValue.current === parentValue) {
            return;
        }
        const hadParentValue = previousParentValue.current !== undefined;
        previousParentValue.current = parentValue;
        if (!hadParentValue) {
            return;
        }
        const parentOptions = optionsBy.options?.[String(parentValue)] ?? [];
        const isOption = (item: unknown) => parentOptions.some((option) => isEqual(option.value, item));
        if (Array.isArray(value)) {
            onChange?.(value.filter(isOption));
        } else if (!isOption(value)) {
            onChange?.(undefined);
        }
    }, [parentValue, value, onChange, optionsBy]);

    return (
        <Select
            value={value}
            onChange={onChange}
            options={options}
            mode={mode}
            placeholder="Select"
            showSearch
            allowClear
            filterOption={filterOptionLabel}
            style={{ width: '100%' }}
        />
    );
};

// Form.Item passes the child value and onChange; the parent is written to its own key
const SettingFieldCascader: React.FC<{
    settingField: SettingField;
    value?: string;
    onChange?: (value: string | undefined) => void;
}> = ({ settingField, value, onChange }) => {
    const form = Form.useFormInstance();
    const parentPath = getSettingFieldNamePath(settingField.optionsBy?.field ?? '');
    const parentValue = Form.useWatch(parentPath);
    const options = getCascaderOptions(settingField);

    return (
        <>
            {/* Registers the parent, which not every definition has as its own field */}
            <Form.Item name={parentPath} initialValue={settingField.currentValue?.[0]} hidden noStyle>
                <Input />
            </Form.Item>
            <Cascader
                allowClear
                expandTrigger="hover"
                placeholder="Select"
                options={options}
                value={value && parentValue ? [parentValue, value] : undefined}
                onChange={(pair) => {
                    form.setFieldValue(parentPath, pair?.[0]);
                    onChange?.(pair?.[1] as string | undefined);
                }}
                style={{ width: '100%' }}
            />
        </>
    );
};

// Parents without children can't complete a pair, so they're disabled
const getCascaderOptions = (settingField: SettingField) => {
    const childOptionsByParent = settingField.optionsBy?.options ?? {};
    return (settingField.options ?? []).map((parentOption) => {
        const children = childOptionsByParent[String(parentOption.value)] ?? [];
        return { ...parentOption, children, disabled: children.length === 0 };
    });
};

// Build Distro bucket rules save the picked workflow as one plain value ([parent, workflow] for a cascader),
// so they get simple inputs built from the release candidate workflow and subworkflow fields
export const getBuildDistroWorkflowPickerFields = (settingFields: SettingField[]): FormFieldData[] => {
    const workflowVariants = settingFields.filter((field) => field.key === 'workflowId');
    const workflowField =
        workflowVariants.find((field) => field.type === SettingFieldType.Cascader) ??
        workflowVariants.find((field) => field.type === SettingFieldType.Select) ??
        workflowVariants[0];
    const subWorkflowField = settingFields.find((field) => field.key === 'subWorkflowId');
    return [workflowField, subWorkflowField]
        .filter((field): field is SettingField => !!field)
        .map((field) => ({
            label: field.label,
            name: field.key,
            fieldType: FieldType.Custom,
            renderField: (_: unknown, extraProperties?: object) =>
                renderBuildDistroWorkflowInput(field, extraProperties),
        }));
};

const renderBuildDistroWorkflowInput = (settingField: SettingField, extraProperties?: object): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Cascader:
            return (
                <Cascader
                    allowClear
                    expandTrigger="hover"
                    placeholder="Select"
                    options={getCascaderOptions(settingField)}
                    {...extraProperties}
                />
            );
        case SettingFieldType.Select:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    {...extraProperties}
                />
            );
        default:
            return <Input autoComplete="off" {...extraProperties} />;
    }
};

const ROWS_SELECT_WIDTH = 112;
const ROWS_INPUT_HEIGHT = 32;

type Row = Record<string, unknown>;

interface RowColumn {
    key: string;
    getPlaceholder: (row: Row) => string;
    // A saved secret shows its placeholder like a value, since the value itself never reaches the browser
    isRedacted?: (row: Row) => boolean;
    options?: SettingOption[];
    isNumber?: boolean;
    tooltip?: string;
    // Makes the column a number input within these bounds
    numberBounds?: { min?: number; max?: number };
}

// Rows of inputs with add and remove buttons; a column with options is a select
const RowsEditor: React.FC<{
    itemLabel: string;
    helpText?: string;
    columns: RowColumn[];
    rows: Row[];
    newRow: Row;
    footer?: React.ReactNode;
    onRowsChange: (rows: Row[]) => void;
}> = ({ itemLabel, helpText, columns, rows, newRow, footer, onRowsChange }) => {
    const hasRows = rows.length > 0;
    return (
        <Space direction="vertical" style={{ width: '100%', paddingBottom: 16 }} size={16}>
            {rows.map((row, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {columns
                                .filter((column) => !column.isNumber)
                                .map((column) =>
                                    column.options ?
                                        <Select
                                            key={column.key}
                                            placeholder={column.getPlaceholder(row)}
                                            options={column.options}
                                            value={row[column.key]}
                                            onChange={(cellValue) =>
                                                onRowsChange(setRowCell(rows, index, column.key, cellValue))
                                            }
                                            style={{ width: ROWS_SELECT_WIDTH, flex: 'none' }}
                                        />
                                    : column.numberBounds ?
                                        <InputNumber
                                            key={column.key}
                                            placeholder={column.getPlaceholder(row)}
                                            min={column.numberBounds.min}
                                            max={column.numberBounds.max}
                                            value={row[column.key] as number | undefined}
                                            onChange={(cellValue) =>
                                                onRowsChange(setRowCell(rows, index, column.key, cellValue))
                                            }
                                            style={{ width: '100%' }}
                                        />
                                    :   <Input
                                            key={column.key}
                                            placeholder={column.getPlaceholder(row)}
                                            classNames={{
                                                input: column.isRedacted?.(row) ? 'redacted-input' : undefined,
                                            }}
                                            value={(row[column.key] as string) ?? ''}
                                            onChange={(event) =>
                                                onRowsChange(setRowCell(rows, index, column.key, event.target.value))
                                            }
                                        />,
                                )}
                        </div>
                        {columns
                            .filter((column) => column.isNumber)
                            .map((column) => {
                                const hasValue = !isNil(row[column.key]);
                                return (
                                    <div
                                        key={column.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            minHeight: ROWS_INPUT_HEIGHT,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Checkbox
                                                checked={hasValue}
                                                onChange={(event) =>
                                                    onRowsChange(
                                                        setRowCell(
                                                            rows,
                                                            index,
                                                            column.key,
                                                            event.target.checked ? 0 : null,
                                                        ),
                                                    )
                                                }
                                                style={hasValue ? undefined : { color: runwayColors.darkTextGrey }}
                                            >
                                                {column.getPlaceholder(row)}
                                            </Checkbox>
                                            {column.tooltip && <TooltipInfoIcon tooltipContent={column.tooltip} />}
                                        </div>
                                        {hasValue && (
                                            <InputNumber
                                                value={row[column.key] as number}
                                                onChange={(cellValue) =>
                                                    onRowsChange(setRowCell(rows, index, column.key, cellValue ?? 0))
                                                }
                                                style={{ width: ROWS_SELECT_WIDTH }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                        onClick={() => onRowsChange(rows.filter((_, i) => i !== index))}
                    />
                </div>
            ))}
            {hasRows && footer}
            <div style={{ display: 'flex', alignItems: 'center', gap: INFO_ICON_GAP, width: 'fit-content' }}>
                <Button
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => onRowsChange([...rows, newRow])}
                    size="small"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                    {hasRows ? `Add another ${itemLabel.toLowerCase()}` : `Add ${itemLabel.toLowerCase()}`}
                </Button>
                {helpText && <TooltipInfoIcon tooltipContent={helpText} />}
            </div>
        </Space>
    );
};

const setRowCell = (rows: Row[], index: number, key: string, cellValue: unknown): Row[] =>
    rows.map((row, i) => (i === index ? { ...row, [key]: cellValue } : row));

// Edits a map as rows; rows without a key are kept while typing but left out of the value
const KeyValueInput: React.FC<{
    itemLabel: string;
    helpText?: string;
    patternTokens?: PatternToken[];
    // Saved secrets come back without values; leaving one empty keeps it
    redactedKeys?: string[];
    value?: Record<string, string>;
    onChange?: (value: Record<string, string>) => void;
}> = ({ itemLabel, helpText, patternTokens, redactedKeys, value, onChange }) => {
    const [rows, setRows] = React.useState<Row[]>(() => getKeyValueRows(value));
    // Resyncs when the form sets the value (reset, new data), but keeps rows the user is still typing a key for
    React.useEffect(() => {
        setRows((currentRows) =>
            isEqual(getKeyValueRowsValue(currentRows), value ?? {}) ? currentRows : getKeyValueRows(value),
        );
    }, [value]);

    const columns: RowColumn[] = [
        { key: 'key', getPlaceholder: () => 'Key' },
        {
            key: 'value',
            getPlaceholder: (row) => (redactedKeys?.includes(row.key as string) ? '••••••••••••' : 'Value'),
            isRedacted: (row) => !!redactedKeys?.includes(row.key as string),
        },
    ];
    const tokensFooter = patternTokens && (
        <Typography.Text type="secondary">
            Tokenized patterns are supported.
            <Popover content={<PatternTokenHelperTextComponent availableTokens={patternTokens} />}>
                <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey, marginInlineStart: INFO_ICON_GAP }} />
            </Popover>
        </Typography.Text>
    );
    return (
        <RowsEditor
            itemLabel={itemLabel}
            helpText={helpText}
            columns={columns}
            rows={rows}
            newRow={{ key: '', value: '' }}
            footer={tokensFooter}
            onRowsChange={(updatedRows) => {
                setRows(updatedRows);
                onChange?.(getKeyValueRowsValue(updatedRows));
            }}
        />
    );
};

const getKeyValueRows = (value: Record<string, unknown> | undefined): Row[] =>
    Object.entries(value ?? {}).map(([key, rowValue]) => ({ key, value: rowValue }));

const getKeyValueRowsValue = (rows: Row[]): Record<string, string> =>
    fromPairs(rows.filter((row) => row.key !== '').map((row) => [row.key as string, (row.value as string) ?? '']));

// Edits a list of objects, one per row, with a column per field of the row
const SettingRowsInput: React.FC<{
    settingField: SettingField;
    value?: Row[];
    onChange?: (value: Row[]) => void;
}> = ({ settingField, value, onChange }) => {
    const columns = settingField.columns ?? [];
    return (
        <RowsEditor
            itemLabel={settingField.itemLabel ?? settingField.label}
            columns={columns.map((column) => ({
                key: column.key,
                getPlaceholder: () => column.placeholder ?? column.label,
                options: column.options ?? undefined,
                isNumber: column.type === SettingFieldType.Number,
                tooltip: column.helpText,
            }))}
            rows={value ?? []}
            newRow={fromPairs(columns.map((column) => [column.key, column.defaultValue]))}
            onRowsChange={(rows) => onChange?.(rows)}
        />
    );
};

// One Form.Item owns each payload key, even when its input changes with another setting.
const conditionalSettingField = (
    formField: FormFieldData,
    variants: SettingField[],
    integrationType: IntegrationType | undefined,
    resolveFormField: SettingFormFieldResolver | undefined,
): FormFieldData => {
    const dependencies = [
        ...new Set(
            flatMap(variants, (field) => [
                ...Object.keys(field.visibleWhenEquals ?? {}),
                ...(field.visibleWhenAnySet ?? []),
                ...Object.keys(field.disabledWhenEquals ?? {}),
                ...(field.disabledUnlessAnySet ?? []),
            ]),
        ),
    ].map(getSettingFieldNamePath);

    return {
        ...formField,
        fieldType: FieldType.Custom,
        skipFormItemWrapper: true,
        renderField: () => (
            <Form.Item noStyle dependencies={dependencies}>
                {({ getFieldValue }) => {
                    const activeField = variants.find((field) => isSettingFieldVisible(field, getFieldValue));
                    const field = formFieldFromSettingField(
                        activeField ?? variants[0],
                        integrationType,
                        resolveFormField,
                    );
                    // A disabled field keeps its value and, like the backend, skips its rules
                    const isDisabled =
                        !!activeField?.disabledReason ||
                        (!!activeField?.disabledWhenEquals &&
                            matchesSettingValues(activeField.disabledWhenEquals, getFieldValue)) ||
                        (!!activeField?.disabledUnlessAnySet?.length &&
                            !isAnySettingSet(activeField.disabledUnlessAnySet, getFieldValue));
                    return (
                        // An explicit false would re-enable fields under a disabled parent, e.g. a notification toggled off
                        <ConfigProvider componentDisabled={isDisabled || undefined}>
                            {renderFormField({
                                ...field,
                                dependencies,
                                rules: activeField && !isDisabled ? field.rules : [],
                                // Keep hidden values registered for submission, without running an inactive input's effects.
                                extraFormItemProps: {
                                    ...field.extraFormItemProps,
                                    hidden: !activeField || field.extraFormItemProps?.hidden,
                                },
                                renderField: activeField ? field.renderField : () => <Input />,
                            })}
                        </ConfigProvider>
                    );
                }}
            </Form.Item>
        ),
    };
};

const isSettingFieldVisible = (settingField: SettingField, getFieldValue: (name: NamePath) => unknown): boolean => {
    const matchesValues = matchesSettingValues(settingField.visibleWhenEquals ?? {}, getFieldValue);
    const visibleWhenAnySet = settingField.visibleWhenAnySet ?? [];
    const hasSetValue = visibleWhenAnySet.length === 0 || isAnySettingSet(visibleWhenAnySet, getFieldValue);
    return matchesValues && hasSetValue;
};

const isEmptySettingValue = (value: unknown): boolean => {
    if (value == null || value === '') return true;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

const matchesSettingValues = (
    conditions: Record<string, unknown>,
    getFieldValue: (name: NamePath) => unknown,
): boolean =>
    Object.entries(conditions).every(([key, expected]) =>
        isEqual(getFieldValue(getSettingFieldNamePath(key)), expected),
    );

const withSettingFieldWarning = (warningText: string | undefined, helpMessage: React.ReactNode): React.ReactNode =>
    warningText ?
        <>
            {helpMessage}
            <Tooltip title={warningText}>
                <WarningFilled style={{ color: runwayColors.statusYellow, marginInlineStart: INFO_ICON_GAP }} />
            </Tooltip>
        </>
    :   helpMessage;

const withSettingFieldResetButton = (settingField: SettingField, helpMessage: React.ReactNode): React.ReactNode =>
    settingField.isResettable ?
        <>
            {helpMessage}
            <SettingFieldResetButton settingField={settingField} />
        </>
    :   helpMessage;

const SettingFieldResetButton: React.FC<{ settingField: SettingField }> = ({ settingField }) => {
    const form = Form.useFormInstance();
    return (
        <div style={{ marginTop: INFO_ICON_GAP }}>
            <Button
                onClick={() => form.setFieldValue(getSettingFieldNamePath(settingField.key), settingField.defaultValue)}
            >
                Reset {settingField.label.toLowerCase()} to default
            </Button>
        </div>
    );
};

// Onboarding and Build Distro show a CI's workflows separately; only CI forms group their fields by workflow type
export const getSeparatedWorkflowFields = (
    settingFields: SettingField[] = [],
    forBuildDistro: boolean,
    integrationType?: IntegrationType,
): {
    workingBranchWorkflowFields: FormFieldData[];
    RCWorkflowFields: FormFieldData[];
    productionWorkflowFields: FormFieldData[];
} => {
    if (forBuildDistro) {
        return {
            workingBranchWorkflowFields: getBuildDistroWorkflowPickerFields(settingFields),
            RCWorkflowFields: [],
            productionWorkflowFields: [],
        };
    }
    if (!integrationType || !settingFields.some((field) => field.group)) {
        return { workingBranchWorkflowFields: [], RCWorkflowFields: [], productionWorkflowFields: [] };
    }

    const fieldsByGroup = groupBy(settingFields, (field) => field.group || WorkflowType.Staging);
    return {
        workingBranchWorkflowFields: formFieldsFromSettingFields(
            fieldsByGroup[WorkflowType.Working] ?? [],
            integrationType,
        ),
        RCWorkflowFields: formFieldsFromSettingFields(fieldsByGroup[WorkflowType.Staging] ?? [], integrationType),
        productionWorkflowFields: formFieldsFromSettingFields(
            fieldsByGroup[WorkflowType.Production] ?? [],
            integrationType,
        ),
    };
};
